"use client";

import { useEffect, useState, useRef } from "react";
import { useRouter } from "next/navigation";
import { ExtraInfo, FormField, StepConfig, WizardConfig, WizardLayout } from "@/types/configuration";
import {
  Button,
  Input,
  InputTel,
  Select,
  TitleHeading,
  trackEvent,
} from "@habacuscode/habacus_ui_component_library";
import PrivacyOptions from "./PrivacyOptions";
import { certificationsHealthCheck, getPromoters, postCookiesSteps, postGenerateOpportunities, postPromoterInvite } from "@/utils/client/client_api";
import SchoolAndCoursesSelector, { SchoolAndCoursesSelectorRef } from "./SchoolAndCoursesSelector";
import { getFirebaseClientAuth } from "@/lib/firebase-client-config";
import MobileStickyBottom from "./MobileStickyBottom";
import { getSessionStorageData, setSessionStorageData } from "@/utils/client/session_storage_utils";
import { CertificationReq, CertificationResp, Consent } from "@/types/typesInterfaces";
import FiscalCodeModal, { fiscalCodeDataName } from "./CodiceFiscaleModal";
import { extractBirthDate, splitFullNameByCF } from "@/utils/codicefiscale";
import { retrieveUtm } from "@/utils/data_steps_utils";
import { Res } from "@/utils/client/common";
import { ROUTE_INTRO, ROUTE_RESULT_KO, ROUTE_RESULT_OK, ROUTE_STEP } from "@/utils/router_utils";
import { useLocale, useTranslations } from "next-intl";
import { LeadReqDTOClidTypeEnum } from "@/generated/@habacus/lead_api/src";
import { getClidType } from "@/utils/client/client_utils";
import NewSchoolAndCoursesSelector from "./NewSchoolAndCoursesSelector";
import ResidenceSelector from "./ResidenceSelector";

type EventLike = {
  target: {
    name: string;
    type: string;
    value: any;
  };
};

const FORM_AUTOSAVE_DEBOUNCE_MS = 400;

function withTelDefaults(
  data: Record<string, any>,
  fields?: FormField[]
): Record<string, any> {
  const result = { ...data };
  fields?.forEach((field) => {
    if (
      field.type === "tel" &&
      result[`${field.name}_country_code`] === undefined
    ) {
      result[`${field.name}_country_code`] = "+39";
    }
  });
  return result;
}

function persistStepToSessionStorage(
  tenant: string,
  version: string,
  currentStep: string,
  formData: Record<string, any>,
  fields?: FormField[]
) {
  const stepDataToSave = withTelDefaults(formData, fields);

  const allData = {
    ...getSessionStorageData(tenant, version),
    [currentStep]: stepDataToSave,
  };
  setSessionStorageData(tenant, version, allData);
  return allData;
}

interface WizardStepProps {
  locale: string;
  tenant: string;
  version: string;
  currentStep: string;
  referralCode?: string;
  schoolCourseTypology?: string;
  totalSteps: number;
  stepConfig: StepConfig;
  firstStep: number;
  nextStepNumber: number;
  prefillValue: any;
  wizardConfig: WizardConfig;
}

export default function WizardStep({
  tenant,
  version,
  currentStep,
  referralCode,
  schoolCourseTypology,
  totalSteps,
  stepConfig,
  firstStep,
  nextStepNumber,
  prefillValue,
  wizardConfig
}: WizardStepProps) {
  const locale = useLocale();
  const router = useRouter();
  const tCommon = useTranslations('Common');
  const [formData, setFormData] = useState<Record<string, any>>({});

  const [isLoading, setIsLoading] = useState(false);
  const [isFiscalCodeModalOpen, setIsFiscalCodeModalOpen] = useState(false);
  const [fiscalCodeError, setFiscalCodeError] = useState<string | null>(null);
  // Traccia se l'utente ha già visto l'errore di incoerenza CF<->nome:
  // al secondo click lo lasciamo comunque proseguire.
  const fiscalCodeMismatchAckRef = useRef(false);

  const [promotersData, setPromotersData] = useState<any>(null);

  const selectorRef = useRef<SchoolAndCoursesSelectorRef | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const hasTrackedStepInteraction = useRef(false);
  const skipAutosaveRef = useRef(true);

  useEffect(() => {
    console.log("prefillValue", prefillValue)
    if (!prefillValue) {
      return;
    }

    let prefillValueJson: Record<string, any>;
    try {
      prefillValueJson = JSON.parse(prefillValue);
      console.log("prefillValueJson", prefillValueJson)
    } catch (err) {
      console.error('Invalid JSON in prefillValue:', err);
      return;
    }

    const currentStorage = getSessionStorageData(tenant, version);
    const updatedStorage = { ...currentStorage };
    let isUpdated: boolean = false;

    for (const [fieldKey, fieldValue] of Object.entries(prefillValueJson)) {
      for (const [stepKey, stepValue] of Object.entries(wizardConfig.steps)) {
        if (!stepValue.fields) {
          // se la field non contiene fields allora nulla da settare
          continue;
        }

        for (let i = 0; i < stepValue.fields.length; i++) {
          if (stepValue.fields[i].name == fieldKey) {
            const stepData = updatedStorage[stepKey] ?? {};
            if (stepData[fieldKey]) {
              // se già esistente non sovrascrivo valore del cookie
              continue;
            }

            updatedStorage[stepKey] = {
              ...stepData,
              [fieldKey]: fieldValue,
            };

            isUpdated = true;
            break;
          }
        }
      }
    }

    if (isUpdated) {
      setSessionStorageData(tenant, version, updatedStorage);
    };
  }, [prefillValue])

  // Update progress bar on client side
  useEffect(() => {
    const progressBar = document.querySelector(
      ".wizard-progress-bar-inner"
    ) as HTMLElement;
    const progressText = document.querySelector(
      ".wizard-progress-text"
    ) as HTMLElement;

    if (progressBar && progressText) {
      const stepNumber =
        currentStep === "result" ? totalSteps : parseInt(currentStep);
      const progress = (stepNumber / (totalSteps - 1)) * 100;

      progressBar.style.width = `${progress}%`;
      progressText.textContent =
        locale === "it"
          ? `Passo ${stepNumber} di ${totalSteps}`
          : `Step ${stepNumber} of ${totalSteps}`;
    }
  }, [currentStep, totalSteps, locale]);

  const getPromotersData = async (code: string) => {
    const res = await getPromoters(code);
    console.log('*** getPromoters response', res)
    if (res.isOk && res.response && res.response.length > 0) {
      const promoter = res.response[0];
      setPromotersData(promoter);
      await handleChange({
        target: {
          name: 'promoterCode',
          type: 'text',
          value: promoter.code
        }
      });
      await handleChange({
        target: {
          name: 'promoterUuid',
          type: 'text',
          value: promoter.uuid
        }
      });
    }
  }

  // Load saved form data if available
  useEffect(() => {
    hasTrackedStepInteraction.current = false;
    skipAutosaveRef.current = true;

    const allData = getSessionStorageData(tenant, version);
    let stepData: Record<string, any> = allData?.[currentStep]
      ? { ...allData[currentStep] }
      : {};
    stepData = withTelDefaults(stepData, stepConfig.fields);

    if (referralCode && parseInt(currentStep) == firstStep) {
      stepData = { ...stepData, referralCode };
      getPromotersData(referralCode);
    }

    setFormData(stepData);

    if (currentStep !== "result" && parseInt(currentStep) > firstStep) {
      // IF step 1 (after initial) load certification server pinging /health check api
      // this should start the serverless server for certification...
      callCertificationHealthCheck();
    }

  }, [tenant, version, currentStep, referralCode]);


  const callCertificationHealthCheck = async () => {
    console.log('*** call health check certification...')
    const res = await certificationsHealthCheck()
    console.log('*** call health check certification RES ', res)
  }

  const handleExtraInfo = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> | EventLike, extraInfo?: string | null) => {
    const { name, type } = e.target;
    let { value } = e.target;
    try {
      extraInfo = extraInfo ? JSON.parse(extraInfo) : null;
    } catch (err) {
      return { name, value, type };
    }

    if (!extraInfo) {
      return { name, value, type };
    }

    const parsedExtraInfo = extraInfo as ExtraInfo;
    if (parsedExtraInfo.uppercase) {
      value = value.toUpperCase();
    }

    return { name, value, type };
  }

  // Debounced autosave: sessionStorage updated while typing (Next still does immediate commit)
  useEffect(() => {
    if (skipAutosaveRef.current) {
      skipAutosaveRef.current = false;
      return;
    }

    const timer = setTimeout(() => {
      persistStepToSessionStorage(
        tenant,
        version,
        currentStep,
        formData,
        stepConfig.fields
      );
    }, FORM_AUTOSAVE_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [formData, tenant, version, currentStep, stepConfig.fields]);

  // Function to handle form field changes (React state only; persistence via autosave / submit)
  const handleChange = async (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> | EventLike) => {
    if (!hasTrackedStepInteraction.current) {
      hasTrackedStepInteraction.current = true;
      await postCookiesSteps(currentStep, window.self !== window.top);
    }

    const rawExtra = (e.target as HTMLInputElement | HTMLSelectElement).getAttribute?.('data-extra');
    const { name, value, type } = handleExtraInfo(e, rawExtra);
    const isCheckbox = type === "checkbox";

    console.log('*** update field', name, 'value', isCheckbox ? (e.target as HTMLInputElement).checked : value, 'is checkbox', isCheckbox,)

    setFormData((prev) => ({
      ...prev,
      [name]: isCheckbox ? (e.target as HTMLInputElement).checked : value,
    }));

    if ((name === "fullName" || name === "fiscalCode")) {
      // Ogni modifica ai campi resetta lo stato dell'errore: l'utente ha di
      // nuovo un tentativo "bloccante" prima di poter forzare l'avanzamento.
      fiscalCodeMismatchAckRef.current = false;
      setFiscalCodeError(null);

      const fullName = name === "fullName" ? value : formData.fullName;
      const fiscalCode = name === "fiscalCode" ? value : formData.fiscalCode;
      if (!fullName || !fiscalCode) {
        return;
      }
      const matches = splitFullNameByCF(fullName, fiscalCode);
      if (matches.length > 0) {
        setFormData((prev) => ({
          ...prev,
          name: matches[0].name,
          surname: matches[0].surname,
        }));
      }
    }
  };

  const callGenerateOpportunities = async (tenant: string, version: string, data: any, bankMetaName?: string): Promise<Res<CertificationResp>> => {
    console.log('*** MAP to DTO from Wizard form data ', data)
    const dataObj = Object.assign({}, ...Object.values(data));
    console.log('*** MAP to DTO from Wizard form data  ', dataObj)

    const bornAt = await extractBirthDate(dataObj.fiscalCode);

    const clidType: LeadReqDTOClidTypeEnum | undefined = getClidType(dataObj);

    const dto: CertificationReq = {
      tenant: tenant,
      bankMetaName: bankMetaName,
      version: version,
      referralCode: referralCode,

      firstName: dataObj.name,
      lastName: dataObj.surname,
      email: dataObj.email,
      bornAt: bornAt!,
      taxCode: dataObj.fiscalCode,
      phoneCountryCode: dataObj.phoneCountryCode ?? dataObj.phone_country_code,
      phoneNumber: dataObj.phoneNumber ?? dataObj.phone_number,

      residenceCountry: dataObj.city.province ? "IT" : "Estero",
      residenceProvince: dataObj.city.province?.code,
      residenceCity: dataObj.city.name,
      residenceCityCode: dataObj.city.code,

      schoolUuid: dataObj.school.id,
      schoolName: dataObj.school.name,
      schoolTypology: dataObj.school.typology,
      schoolCourseTypology: dataObj.course.id,
      schoolProvince: dataObj.school.province,
      schoolCity: dataObj.school.cityName,
      schoolCityCode: dataObj.school.cityCode,

      latestTitle: dataObj.course_previous?.id,
      latestTitleCountry: "IT",
      latestFinalGrade: dataObj.vote?.value,
      latestFinalGradeHasLaude: dataObj.vote?.extra?.isCumLaude ?? false,

      yearActual: dataObj.year_actual?.value,

      consents: dataObj.consents,

      clid: dataObj.gclid
        ?? dataObj.dclid
        ?? dataObj.fbclid
        ?? dataObj.msclkid
        ?? dataObj.twclid
        ?? dataObj.ttclid
        ?? dataObj.li_fat_id
        ?? dataObj.yclid
        ?? dataObj.ef_id
        ?? dataObj.mc_eid,

      clidType: clidType,

      utmId: retrieveUtm(dataObj, 'id'),
      utmSource: retrieveUtm(dataObj, 'source'),
      utmMedium: retrieveUtm(dataObj, 'medium'),
      utmCampaign: retrieveUtm(dataObj, 'campaign'),
      utmTerm: retrieveUtm(dataObj, 'term'),
      utmContent: retrieveUtm(dataObj, 'content'),
    }

    // call it
    console.log('*** pre cert call payload sent:', dto);
    const resp: Res<CertificationResp> = await postGenerateOpportunities(dto);
    console.log('*** pre cert call response:', resp);

    if (dataObj.promoterUuid) {
      try {
        await postPromoterInvite({
          promoterUuid: dataObj.promoterUuid,
          promoterInviteCreateResource: {
            code: dataObj.referralCode,
            name: dataObj.name,
            surname: dataObj.surname,
            taxCode: dataObj.fiscalCode,
            email: dataObj.email,
            phoneCountryCode: dataObj.phoneCountryCode,
            phone: dataObj.phoneNumber,
            privacy: true
          },
        });
      } catch (error) {
        console.log(error)
      }
    }

    return resp;
  }

  // Function to handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (stepConfig.id == "school_info") {
      const isValid = selectorRef.current?.validate() ?? true;
      if (!isValid) {
        return;
      }
    }

    if (stepConfig.id == "personal_data") {
      const formEl = formRef.current;
      if (!formEl) {
        console.error("formRef non è definito");
        return;
      }
      if (!formEl.checkValidity()) {
        formEl.reportValidity();
        return;
      }

      const fullName = formData.fullName;
      const fiscalCode = formData.fiscalCode;
      if (fullName && fiscalCode) {
        const matches = splitFullNameByCF(fullName, fiscalCode);
        if (matches.length === 0) {
          if (!fiscalCodeMismatchAckRef.current) {
            fiscalCodeMismatchAckRef.current = true;
            setFiscalCodeError(tCommon('taxCode.mismatch'));
            return;
          } else {
            setFormData((prev) => ({
              ...prev,
              name: fullName,
              surname: fullName,
            }));
          }
        }

        setFiscalCodeError(null);
      }
    }

    setIsLoading(true);

    try {
      const allData = persistStepToSessionStorage(
        tenant,
        version,
        currentStep,
        formData,
        stepConfig.fields
      );

      const res = await postCookiesSteps(currentStep, window.self !== window.top);
      console.log('*** postCookiesSteps response', res)

      // Navigate to the next step or result page
      const currentStepNumber = parseInt(currentStep);

      const firebaseClientAuth = await getFirebaseClientAuth();
      if (!firebaseClientAuth) {
        throw new Error('firebaseClientAuth is null');
      }

      trackEvent('form_next', {
        firebase_current_user_id: firebaseClientAuth.currentUser?.uid,
        firebase_current_user_status: "anonymous",
        current_step: currentStepNumber,
        next_step: nextStepNumber,
      });

      if (currentStepNumber != nextStepNumber && nextStepNumber <= totalSteps) {
        router.replace(`/${locale}/${tenant}/${version}/steps/${nextStepNumber}`);
      } else {
        const resp: Res<CertificationResp> = await callGenerateOpportunities(tenant, version, allData, wizardConfig.bankMetaName);
        const isOk: boolean = resp.isOk && resp.response!.numberMatchingProducts > 0;

        try {
          trackEvent('form_end', {
            firebase_current_user_id: firebaseClientAuth.currentUser?.uid,
            firebase_current_user_status: "anonymous",
            execution_id: resp.response?.id,
            result: isOk,
          });
        } catch (error) {
          console.log(error)
        }
        const url = isOk ? ROUTE_RESULT_OK(locale, tenant, version, resp.response!.id) : ROUTE_RESULT_KO(locale, tenant, version, resp.response!.id);
        router.replace(url);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to go back to the previous step
  const handleBack = () => {
    const prevStepNumber = Math.max(firstStep, parseInt(currentStep) - 1);
    let url = '';
    if (prevStepNumber === firstStep) {
      url = ROUTE_INTRO(locale, tenant, version);
    } else {
      url = ROUTE_STEP(locale, tenant, version, prevStepNumber);
    }
    router.replace(url);
  };

  const renderFields = () => {
    return (<>
      {stepConfig.fields?.map((field: FormField) => (
        <div key={field.name} className={field.type === "fiscalCodeHelperTool" ? "" : "form-field"}>
          {field.type === "link" && (
            <a
              className="no-underline"
              href="#"
              id={field.name}
              style={{ cursor: "pointer" }}
              onClick={(e) => console.log(`step Link clicked`, e)}
            >
              <div className="border border-gray-200 rounded-lg p-6 mb-4 hover:bg-gray-50 transition-bg hover:border-gray-300 transition-border duration-300 relative cursor-pointer group">
                <div className="flex justify-between items-start">
                  <div className="pr-8">
                    <h3 className="text-blue-950 font-medium">
                      {field.label[locale]}
                    </h3>
                  </div>
                  <div className="right-4 text-gray-500 ">
                    <svg width="24" height="24" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 15L15 5M15 5H8.33333M15 5V11.6667" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </div>
            </a>
          )}

          {(field.type === "text" || field.type === "number" || field.type === "email") && (
            <>
              <Input
                data-extra={JSON.stringify(field.extra)}
                label={field.label[locale]}
                type={field.inputType}
                id={field.name}
                name={field.name}
                placeholder={field.placeholder?.[locale]}
                autoComplete={field.autocomplete}
                required={field.required}
                onChange={handleChange}
                value={formData[field.name] || ''}
                classNameContainer="select"
                minLength={field.requirements?.minLength}
                maxLength={field.requirements?.maxLength}
                min={field.requirements?.min}
                max={field.requirements?.max}
                pattern={field.requirements?.pattern}
              />
              {field.name === "fiscalCode" && fiscalCodeError && (
                <p className="mt-1 ml-1 text-left text-sm/6 text-red-600">
                  {fiscalCodeError}
                </p>
              )}
            </>
          )}

          {field.type === "tel" && (
            <InputTel
              label={field.label[locale]}
              id={field.name}
              autoComplete={"tel-local"}
              name={field.name}
              required={field.required}
              placeholder={field.placeholder?.[locale]}
              onChangeAreaCode={handleChange}
              onChangeNumber={handleChange}
              value_area_code={formData[`${field.name}CountryCode`] ?? "+39"}
              value_number={formData[`${field.name}Number`]}
              className="select"
            />
          )}

          {field.type === "city" && (
            <ResidenceSelector
              id={field.name}
              name={field.name}
              required={field.required}
              label={field.label[locale]}
              defaultValue={formData[field.name] || ''}
              locale={locale}
              onChange={(e) => {
                handleChange({
                  target: {
                    name: field.name,
                    type: 'select',
                    value: e.value,
                  },
                })
              }
              }
            />
          )}

          {field.type === "select" && (
            <Select
              id={field.name}
              name={field.name}
              required={field.required}
              label={field.label[locale]}
              defaultValue={formData[field.name] || ''}
              onChange={(e) =>
                handleChange({
                  target: {
                    name: field.name,
                    type: 'select',
                    value: e.value,
                  },
                })
              }
              idKey="value"
              labelKey="label"
              items={
                field.options?.map((option) => {
                  return {
                    label: option.label[locale],
                    value: option.value,
                  };
                }) || []
              }
            >
              {/*}
                <option value="">Select...</option>
                {field.options?.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label[locale]}
                  </option>
                  */}
            </Select>
          )}

          {field.type === "checkbox" && (
            <>
              <label htmlFor={field.name}>{field.label[locale]}</label>
              <input
                type="checkbox"
                id={field.name}
                name={field.name}
                required={field.required}
                onChange={handleChange}
                value={formData[field.name] || ''}
              />
            </>
          )}

          {field.type === "fiscalCodeHelperTool" && (
            <p className="mt-1 ml-1 text-left text-sm/6 text-blue-950">
              {tCommon('taxCode.tool.noRemeber')}&nbsp;
              <a
                href="#"
                onClick={() => {
                  setIsFiscalCodeModalOpen(true);
                }}
                className="text-blue-950 decoration-wavy decoration-2 decoration-blue-600 underline-offset-4"
              >
                {tCommon('taxCode.tool.useTool')}
              </a>
            </p>
          )}

          {field.type === "custom" && (
            <>{field.children}</>
          )}

          {field.type === "schoolAndCourseSelector" && (
            <>
              <SchoolAndCoursesSelector
                ref={selectorRef}
                locale={locale}
                fixedSchools={field.schools}
                residence={formData['city']}
                school={formData['school']}
                course={formData['course']}
                year={formData['year_actual']}
                coursePrevious={formData['course_previous']}
                vote={formData['vote']}
                onChangeData={(e: any, name: string) => {
                  handleChange({
                    target: {
                      name: name,
                      type: 'text',
                      value: e
                    }
                  });
                }}
              />
            </>
          )}

          {field.type === "newSchoolAndCourseSelector" && (
            <>
              <NewSchoolAndCoursesSelector
                ref={selectorRef}
                locale={locale}
                fixedSchools={field.schools}
                residence={formData['city']}
                school={formData['school']}
                course={formData['course']}
                year={formData['year_actual']}
                coursePrevious={formData['course_previous']}
                fromUrlSchoolCourseTypology={schoolCourseTypology}
                vote={formData['vote']}
                onChangeData={(e: any, name: string) => {
                  handleChange({
                    target: {
                      name: name,
                      type: 'text',
                      value: e
                    }
                  });
                }}
              />
            </>
          )}

          {field.type === "privacyOptions" && (
            <>
              <PrivacyOptions
                consents={formData['consents']}
                onChange={(options: Array<Consent>) => {
                  console.log('*** handle change privacy options ', options)
                  // update all form data with all options updated states
                  setFormData(formData => ({
                    ...formData,
                    consents: options,
                  }));
                }}
              />
            </>
          )}
        </div>
      ))
      }</>
    );
  }

  const renderExtraFields = () => {
    const fields: FormField[] | undefined = stepConfig.fields?.filter(p => p.type == "fiscalCodeHelperTool");
    if (!fields || fields.length === 0) {
      return;
    }

    const field = fields[0];

    return (
      <div key={field.name} className="form-field">
        {field.type === "fiscalCodeHelperTool" && isFiscalCodeModalOpen && (
          <FiscalCodeModal
            onClose={() => setIsFiscalCodeModalOpen(false)}
            locale={locale}
            onChangeData={(e: any, name: string) => {
              handleChange({
                target: {
                  name: name,
                  type: 'text',
                  value: e
                }
              });
            }}
            data={
              {
                firstName: formData[fiscalCodeDataName.firstName],
                lastName: formData[fiscalCodeDataName.lastName],
                gender: formData[fiscalCodeDataName.gender],
                birthDate: {
                  day: formData[fiscalCodeDataName.birthDate.day],
                  month: formData[fiscalCodeDataName.birthDate.month],
                  year: formData[fiscalCodeDataName.birthDate.year]
                },
                birthPlace: {
                  country: formData[fiscalCodeDataName.birthPlace.country],
                  place: formData[fiscalCodeDataName.birthPlace.place]
                }
              }
            }
            onConfirm={(fiscalCode) => {
              console.log('*** handle change fiscal code tool ', fiscalCode)
              handleChange({
                target: {
                  name: 'fiscalCode',
                  type: 'text',
                  value: fiscalCode
                }
              });

              setIsFiscalCodeModalOpen(false)
            }}
          />
        )
        }
      </div>
    );
  }

  const renderButtons = () => {
    return (
      <div className={`wizard-buttons md:mt-8 ${stepConfig.layout == WizardLayout.BUTTON_FIELDS ? 'mb-0 md:mb-8' : ''}`} >
        <div className="hidden md:block">
          <Button name="wizard_next" variant="primary" type="submit" size="md" className="next-button" loading={isLoading}>
            {stepConfig.nextButtonText?.[locale] || "Next"}
          </Button>
        </div>

        <MobileStickyBottom>
          <Button name="wizard_next" variant="primary" type="submit" size="md" className="w-full justify-center py-4" loading={isLoading} >
            {stepConfig.nextButtonText?.[locale] || "Next"}
          </Button>
        </MobileStickyBottom>
      </div>
    )
  }

  return (
    <div className="wizard-step flex flex-col justify-center align-middle">
      {parseInt(currentStep) > firstStep && (
        <div className="mb-3 md:mb-5">
          <Button name="wizard_back" variant="outline" type="button" className="back-button" onClick={handleBack} size="sm" icon="fas fa-chevron-left" loading={isLoading}>
            {stepConfig.backButtonText?.[locale] || "Back"}
          </Button>
        </div>
      )}

      {promotersData && parseInt(currentStep) == firstStep &&
        <div className="w-full flex justify-left ">
          <span className="w-fit px-5 gap-2 py-1 rounded-full text-md border border-blue-950 text-blue-950">
            {"Invito ricevuto da " + promotersData.name}!
          </span>
        </div>
      }

      <TitleHeading label={stepConfig.title[locale]} size="h1" className="my-3" />
      <TitleHeading label={stepConfig.subTitle?.[locale] ?? ""} size="h2" className={(parseInt(currentStep) == firstStep ? "pt-4 lg:pt-0" : "")} />
      {stepConfig.description?.[locale] &&
        <TitleHeading label={stepConfig.description[locale]} size={stepConfig.subTitle?.[locale] ? "h3" : "h2"} className={(parseInt(currentStep) == firstStep ? "pb-4 lg:pb-6" : "mb-6")} />
      }

      <form ref={formRef} onSubmit={handleSubmit}>
        {stepConfig.layout == WizardLayout.BUTTON_FIELDS ? renderButtons() : null}
        {renderFields()}
        {stepConfig.layout == WizardLayout.FIELDS_BUTTONS ? renderButtons() : null}
      </form>
      {renderExtraFields()}
    </div>
  );
}
